(ns arthur.domain.retime
  "Changing the document's rate.

  A symbol is a bag of frames and the document has one rate, so there are two
  honest things a new rate can mean, and they are Flash's two:

    `(assoc clip :fps rate)`  KEEP FRAMES. Every frame stays the frame it was and
                              plays for the new tick, so the animation runs
                              faster or slower. Sounds still play in seconds.

    `(scale-spans clip rate)` KEEP TIME. Every frame number is moved to where the
                              same moment falls on the new grid — Animate's
                              \"scale spans\". Going down, frames that land on
                              one another are dropped and the later wins, as it
                              would have been showing; going up, holds just get
                              longer, because nothing is invented.

  ONE RULE FOR EVERY NUMBER: it is multiplied by the factor of the frame space it
  lives in. An authored symbol's factor is `rate/old`. A symbol whose frames are
  the CAMERA's — footage, or measurements taken from it — cannot be rewritten
  frame by frame, so its factor is 1 and its placements absorb the change: an
  instance's speed becomes `speed · child/host`, which is the same formula that
  scales everything else, read the other way round."
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.node :as node]))

(defn- camera?
  "Are `sym`'s frames the camera's: footage, or anything measured from it?"
  [sym]
  (or (clip/trace? sym)
      (some (fn [n] (some :dense (vals (:channels n)))) (vals (:nodes sym)))))

(defn- at [k f] (js/Math.round (* k f)))

(defn- interval
  "Half-open `[in out)` scaled, never collapsing to nothing."
  [k [in out]]
  (let [in' (at k in)] [in' (max (inc in') (at k out))]))

(defn- by-frame
  "Frame-keyed map `m` re-keyed. Sorted first, so where two keys land on one
  frame the later one wins."
  [k m]
  (into {} (map (fn [[f v]] [(at k f) v])) (sort-by key m)))

(defn- channel [k c]
  (cond-> c
    (:keys c)     (update :keys #(by-frame k %))
    (:segments c) (update :segments #(by-frame k %))
    (seq (:over c))
    (update :over (partial mapv #(cond-> (update % :values (partial channel k))
                                   (vector? (:support %)) (update :support (partial interval k)))))))

(defn- channels [k chs]
  (into {} (map (fn [[p c]] [p (channel k c)])) chs))

(defn- time-map
  "`:at` is in the parent's frames; the floor, holds and lead are the node's own."
  [kp ko t]
  (cond-> t
    (:at t)     (update :at #(at kp %))
    (:offset t) (update :offset #(at ko %))
    (:expose t) (update :expose #(max 1 (at ko %)))
    (:holds t)  (update :holds (partial mapv #(at ko %)))))

(defn- scale-node
  "`kh` is the factor of the symbol `n` is in, `kc` of the one it places and `k`
  the document's. A sound's own frames are its source's, which the document's
  rate converts, so only its place moves and its rate takes the document's
  change back out where the host did not follow it."
  [kh kc k n]
  (case (:kind n)
    :audio
    (cond-> (update n :time #(time-map kh 1 %))
      (not= k kh) (update :time #(update (merge {:rate 1} %) :rate * (/ k kh))))

    (cond-> n
      (:time n)     (update :time #(time-map kh kh %))
      (:channels n) (update :channels #(channels kh %))
      (:span n)     (update :span #(interval kh %))
      (and (= :instance (:kind n)) (or (:playback n) (not= kc kh)))
      (update :playback
              (fn [{:keys [in speed tracks] :as p :or {in 0 speed 1}}]
                (cond-> (assoc p :in (at kc in) :speed (* speed (/ kc kh)))
                  tracks (assoc :tracks
                                (into {} (map (fn [[g entries]]
                                                [g (into {} (map (fn [[f s]] [(at kh f) (at kc s)]))
                                                         (sort-by key entries))]))
                                      tracks))))))))

(defn scale-spans
  "`clip` at `rate`, with every authored frame moved so it happens when it did."
  [clip rate]
  (let [k      (/ rate (:fps clip))
        factor (into {} (map (fn [[sid sym]] [sid (if (camera? sym) 1 k)])) (:symbols clip))]
    (-> clip
        (assoc :fps rate)
        (update :symbols
                #(into {}
                       (map (fn [[sid sym]]
                              (let [kh (factor sid)]
                                [sid (cond-> (-> sym
                                                 (update :frames (fn [n] (max 1 (at kh n))))
                                                 (update :nodes (fn [ns] (into {} (map (fn [[id n]] [id (scale-node kh (get factor (node/source n) 1) k n)])) ns))))
                                       (:palette-channel sym) (update :palette-channel (partial channel kh)))])))
                       %)))))
